import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGetMyNodesQuery } from '../store/api/nodesApi';
import { useGetAllMapNodesQuery, useDeleteMapNodeMutation } from '../store/api/mapNodesApi';
import AddNodeModal from './AddNodeModal';
import Snackbar from './Snackbar';
import '../styles/NodesSidebar.css';

const NodesSidebar = ({ isOpen, onToggle, onNodeClick, sortedNodes, sortBy, onMapClickRequest }) => {
  const { isAuthenticated, user } = useAuth();
  const [contextMenu, setContextMenu] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  
  const { data: myNodesData } = useGetMyNodesQuery(undefined, {
    skip: !isAuthenticated,
  });
  
  const [deleteMapNode] = useDeleteMapNodeMutation();

  const myNodeIds = myNodesData?.nodeIds || [];
  
  // Use sorted nodes passed from parent, or fetch if not provided
  const { data: fetchedNodes = [] } = useGetAllMapNodesQuery({}, {
    skip: !!sortedNodes,
  });
  const allNodes = sortedNodes || fetchedNodes;
  
  // Get full node details for user's nodes
  const myNodes = allNodes.filter(node => myNodeIds.includes(node.nodeId));
  const otherNodes = allNodes.filter(node => !myNodeIds.includes(node.nodeId));

  const handleContextMenu = (e, nodeId, isOwnNode) => {
    e.preventDefault();
    setContextMenu({
      nodeId,
      x: e.clientX,
      y: e.clientY,
      isOwnNode,
    });
  };

  const handleRemoveNode = async (nodeId) => {
    try {
      // Check if user owns this node (added it to the map)
      const userId = user?.id || user?.Id;
      const node = allNodes.find(n => n.nodeId === nodeId);
      
      if (node && node.addedByDiscordUserId === userId) {
        // Delete from map (this is the main action)
        await deleteMapNode(nodeId).unwrap();
        setSnackbar({ message: 'Node removed from map successfully', type: 'success' });
      } else {
        setSnackbar({ message: 'You can only remove nodes you added', type: 'error' });
      }
      
      setContextMenu(null);
    } catch (err) {
      console.error('Error removing node:', err);
      const errorMsg = err?.data?.message || err?.message || 'Failed to remove node';
      setSnackbar({ message: errorMsg, type: 'error' });
    }
  };

  const handleCopyNodeId = (nodeId) => {
    // Copy with meshcore:// prefix for easy sharing
    const fullNodeId = `meshcore://${nodeId}`;
    navigator.clipboard.writeText(fullNodeId).then(() => {
      setSnackbar({ message: 'Node ID copied to clipboard!', type: 'success' });
      setContextMenu(null);
    }).catch(() => {
      setSnackbar({ message: 'Failed to copy to clipboard', type: 'error' });
    });
  };

  const handleEditNode = (nodeId) => {
    const node = allNodes.find(n => n.nodeId === nodeId);
    const userId = user?.id || user?.Id;
    
    if (node && node.addedByDiscordUserId === userId) {
      setEditingNode(node);
      setContextMenu(null);
    } else {
      setSnackbar({ message: 'You can only edit nodes you added', type: 'error' });
      setContextMenu(null);
    }
  };

  const handleNodeClick = (node) => {
    if (onNodeClick && node.latitude && node.longitude) {
      onNodeClick(node.latitude, node.longitude);
    }
  };

  const handleClickOutside = () => {
    setContextMenu(null);
  };

  React.useEffect(() => {
    if (contextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu]);

  return (
    <>
      <button className={`sidebar-toggle ${isOpen ? 'open' : ''}`} onClick={onToggle}>
        {isOpen ? '◀' : '▶'}
      </button>

      <div className={`nodes-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header p-5 has-background-light">
          <h3 className="title is-4">Nodes</h3>
        </div>

        {isAuthenticated && myNodes.length > 0 && (
          <>
            <div className="sidebar-section p-4">
              <h4 className="subtitle is-6 has-text-grey">My Nodes</h4>
              <ul className="node-list">
                {myNodes.map((node) => (
                  <li
                    key={node.nodeId}
                    className="node-item my-node box p-3 mb-2"
                    onClick={() => handleNodeClick(node)}
                    onContextMenu={(e) => handleContextMenu(e, node.nodeId, true)}
                  >
                    <div className="node-info">
                      <div className="has-text-weight-semibold">{node.friendlyName || node.nodeHash}</div>
                      <div className="is-size-7 has-text-grey">
                        <span>👥 {node.contactCount || 0}</span>
                        <span className="ml-2">📍 {node.latitude?.toFixed(3)}, {node.longitude?.toFixed(3)}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="sidebar-divider"></div>
          </>
        )}

        <div className="sidebar-section p-4">
          <h4 className="subtitle is-6 has-text-grey">All Nodes ({otherNodes.length})</h4>
          <ul className="node-list">
            {otherNodes.map((node) => {
              // Backend returns capitalized property names (C# convention)
              const userId = user?.id || user?.Id;
              const isOwnNode = node.addedByDiscordUserId === userId;
              return (
                <li 
                  key={node.nodeId} 
                  className={`node-item ${isOwnNode ? 'own-node' : ''} box p-3 mb-2`}
                  onClick={() => handleNodeClick(node)}
                  onContextMenu={(e) => handleContextMenu(e, node.nodeId, isOwnNode)}
                >
                  <div className="node-info">
                    <div className="has-text-weight-semibold">{node.friendlyName || node.nodeHash}</div>
                    <div className="is-size-7 has-text-grey">
                      <span>👥 {node.contactCount || 0}</span>
                      <span className="ml-2">by {node.addedByDiscordUsername}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {!isAuthenticated && (
          <div className="has-text-centered p-6 is-italic" style={{ color: '#aaa' }}>
            Login to add your nodes
          </div>
        )}
      </div>

      {contextMenu && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button 
            className="is-info"
            onClick={() => handleCopyNodeId(contextMenu.nodeId)}
          >
            📋 Copy Node ID
          </button>
          {contextMenu.isOwnNode && (
            <>
              <button 
                className="is-warning"
                onClick={() => handleEditNode(contextMenu.nodeId)}
              >
                ✏️ Edit Node
              </button>
              <button 
                className="is-danger"
                onClick={() => handleRemoveNode(contextMenu.nodeId)}
              >
                🗑️ Remove Node
              </button>
            </>
          )}
        </div>
      )}

      {snackbar && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar(null)}
        />
      )}

      {editingNode && (
        <AddNodeModal
          editNode={editingNode}
          onClose={() => setEditingNode(null)}
          onNodeAdded={() => {
            setEditingNode(null);
            setSnackbar({ message: 'Node updated successfully!', type: 'success' });
          }}
          onMapClickRequest={onMapClickRequest}
        />
      )}
    </>
  );
};

export default NodesSidebar;

